import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  IconButton,
  Box,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  NotificationsOutlined as BellIcon,
} from '@mui/icons-material';

import useSessionStore from '../../../../hooks/useSessionStore';
import NavigationContext, { NavigationRouteType } from '../../../context/NavigationContext';
import LayoutContext from '../../context/LayoutContext';
import OnboardingCoachNotificationsContext from '../../../context/OnboardingCoachNotificationsContext';
import ExternalCoachContext from '../../../context/ExternalCoachContext';
import UserContext from '../../../../context/UserContext';
import ActionItemsContext from '../../../context/ActionItemsContext';
import ActionItemsDrawer from '../ActionItemsDrawer/ActionItemsDrawer';
import shouldShowRightPanel from '../../utils/shouldShowRightPanel';
import Account from '../Account';
import ViewAs from '../ViewAs';
import CoachReminder from '../CoachReminder';
import CreateCoachTask from '../CreateCoachTask';
import PodSelector from '../PodSelector/PodSelector';
import OnboardingChecklistAlert from '../../OnboardingChecklistAlert';
import { COLLAPSED_BAR_WIDTH } from '../RightPanel';
import CoachTimeClock from '../../../../components/CoachTimeClock';
import MessagingBroadcastButton from '../../../pages/Chat/components/MessagingBroadcast';
import {
  StyledAppBar,
  StyledToolbar,
} from './styles';

const Header = ({
  showMenuIcon,
}) => {
  const {
    title,
    toggleDrawer,
    isDrawerOpen,
    rightPanelWidth,
    isChatPanelOpen,
  } = useContext(LayoutContext);
  const { routeType } = useContext(NavigationContext);
  const { missingSettingsActions } = useContext(OnboardingCoachNotificationsContext);
  const { coachDoc } = useContext(ExternalCoachContext);

  const { userDoc } = useContext(UserContext);
  const { unreadCount } = useContext(ActionItemsContext);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { isAdmin } = useSessionStore();
  const hasCoachTimeZone = !!coachDoc?.timeZone;
  const showNotificationsBell = userDoc?.isSDR || userDoc?.isInsideSales;

  const showOnboardingChecklistAlert = (routeType === NavigationRouteType.COACH && missingSettingsActions.length > 0);
  const showRightPanel = shouldShowRightPanel({ routeType });

  // Calculate right panel width for header adjustment
  const getHeaderRightOffset = () => {
    if (!showRightPanel) {
      return 0;
    }
    if (!isChatPanelOpen) {
      return COLLAPSED_BAR_WIDTH;
    }
    return rightPanelWidth;
  };

  return (
    <>
      <StyledAppBar
        position="fixed"
        open={isDrawerOpen}
        $rightPanelWidth={getHeaderRightOffset()}
      >
        <StyledToolbar $hasWarning={showOnboardingChecklistAlert}>
          {showMenuIcon && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{
                mr: 2,
                ...(isDrawerOpen && { display: 'none' }),
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ mr: 2 }}>
            {title}
          </Typography>
          {routeType === NavigationRouteType.COACH && <MessagingBroadcastButton />}
          {/* Spacer to push items to the right */}
          <Box sx={{ flexGrow: 1 }} />
          {routeType === NavigationRouteType.COACH && (
            <>
              {hasCoachTimeZone && <CoachTimeClock timeZone={coachDoc.timeZone} />}
              <CoachReminder />
              <CreateCoachTask />
              <PodSelector />
            </>
          )}
          {showNotificationsBell && (
            <IconButton color="inherit" onClick={() => setDrawerOpen((prev) => !prev)}>
              <Badge badgeContent={unreadCount} color="error">
                <BellIcon />
              </Badge>
            </IconButton>
          )}
          {isAdmin && <ViewAs />}
          <Account />
        </StyledToolbar>
        {showOnboardingChecklistAlert && <OnboardingChecklistAlert />}
      </StyledAppBar>
      {showNotificationsBell && (
        <ActionItemsDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      )}
    </>
  );
};

Header.propTypes = {
  showMenuIcon: PropTypes.bool,
};

Header.defaultProps = {
  showMenuIcon: true,
};

export default Header;
